import React, { useEffect, useState, useCallback } from "react";

// region Imports - Graphql
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  TInvoiceAllWithSortedPaginationQueries, invoiceAllWithSortedPagination
} from "@graphql/groups/invoice/query.graphql";
import {
  InvoiceType,
  ECommandResultStatus,
  EWhereClausesOperators,
  ESort,
  PermissionUserQueriesGetAllWithoutPaginationArgs,
  InvoiceQueriesGetAllWithSortedPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
// endregion Imports - Graphql
// region Imports - Telluria Utils
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Imports - Telluria Utils
// region Imports - Languages
import { AlertMessages, CustomerMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Imports - Languages
// region Imports - Hooks
import { useAuth } from "@hooks/useAuth";
// endregion Imports - Hooks
// region Imports - Stores
import Store from "@store/jotai";
import { ModulesID } from "@store/constants/index";
// endregion Imports - Stores
// region Imports - Libraries
import { Grid } from "@libraries/mui";
// endregion Imports - Libraries
// region Imports - Helpers
import utils from "@helpers/utils";
// endregion Imports - Helpers
// region Imports - Atoms
import AlertContentByClient from "@atoms/AlertContentByClient";
import AlertDialog from "@atoms/AlertDialog";
import { useSelectClient } from "@atoms/SelectClient";
import { Loading } from "@atoms/index";
import type { IInvoiceFiscalDocumentArchives } from "@atoms/index";
import ShowMoreButton from "@atoms/ShowMoreButton";
// endregion Imports - Atoms
// region Imports - Molecules
import {
  PageHeader,
  BaseCard,
  BaseCardItem,
  BaseCardHeader,
  BaseCardAction
} from "@molecules/index";
// endregion Imports - Molecules
// region Imports - Pages
import Office from "../Office";
// endregion Imports - Pages
// region Imports - Styles
import * as Styled from "./styles";
// endregion Imports - Styles

// region Interfaces
interface WhereInvoiceIdsByClient {
  clientId: string;
  invoiceIds: string;
}
// endregion Interfaces

const Invoice: React.FC = () => {

  // region Hooks
  const { t } = useTranslation();

  const {
    value: invoices,
    setValue: setInvoices,
    filteredInvoices,
    setFilteredInvoices
  } = Store.useInvoices();

  const { value: selectedClient } = useSelectClient();

  const { addToast } = useToast();

  const { getDecodedToken } = useAuth();
  // endregion Hooks
  // region States
  const [whereInvoiceIdByClients, setWhereInvoiceIdByClients] = useState<WhereInvoiceIdsByClient[]>([]);

  const [nextPage, setNextPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [alertDialogMessage, setAlertDialogMessage] = useState<string>("");
  const [isPermissionDialogAlert, setIsPermissionDialogAlert] = useState<boolean>(false);

  const [noPermissionInCurrentClient, setNoPermissionInCurrentClient] = useState<boolean>(false);
  const [noDataInCurrentClient, setNoDataInCurrentClient] = useState<boolean>(false);
  // endregion States
  // region Constants

  const token = getDecodedToken();

  const limitPerPage = 9;
  // endregion Constants
  // region GraphQL - Queries
  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.invoices.id", "construction.client.id"]), {
    fetchPolicy: "network-only",
    pollInterval: 60000,
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.INVOICE
        }
      ]
    }
  });

  const [
    getInvoices,
    { loading: loadingInvoices, error: errorInvoices, data: dataInvoices }
  ] = useLazyQuery<
    TInvoiceAllWithSortedPaginationQueries,
    InvoiceQueriesGetAllWithSortedPaginationArgs
  >(
    invoiceAllWithSortedPagination([
      "id",
      "status",
      "invoiceNumber",
      "issueDate",
      "archive.id",
      "archive.originalName",
      "archive.extension",
      "contract.code",
      "contract.construction.name",
      "invoiceInstallments.id",
      "invoiceInstallments.installmentNumber",
      "invoiceInstallments.status",
      "invoiceInstallments.dueDate",
      "invoiceInstallments.netValue",
      "invoiceInstallments.settlementDate",
      "invoiceInstallments.amountPaid",
      "invoiceInstallments.withholdings",
      "invoiceInstallments.archive.id",
      "invoiceInstallments.archive.originalName",
      "invoiceInstallments.archive.extension",
      "fiscalDocument.archive.id",
      "fiscalDocument.archive.originalName",
      "fiscalDocument.archive.extension",
      "fiscalDocument.archive.storageUrl",
      "fiscalDocument.fiscalDocumentNumber"
    ]),
    {
      fetchPolicy: "network-only",
      pollInterval: 60000
    }
  );
  // endregion Graphql - Queries
  // region Functions
  /**
   * Render the invoice details
   */
  const detailsInvoice = useCallback((invoice: InvoiceType) => {
    if (!invoice.invoiceInstallments) return undefined;

    return (
      <div>
        {invoice.invoiceInstallments
          .sort((a, b) => (a?.installmentNumber < b?.installmentNumber ? -1 : 1))
          .map((invoiceInstallment) => {
            if (!invoiceInstallment) return undefined;

            return (
              <BaseCardItem
                key={invoiceInstallment.id}
                type="invoiceInstallment"
                invoiceInstallment={invoiceInstallment}
              />
            );
          })}
      </div>
    );
  }, []);

  const handleNextPage = () => setNextPage(nextPage + 1);

  /**
   * Render the actions button of the invoice card
   */
  const actionInvoice = useCallback((invoice: InvoiceType) => {
    if (!invoice.archive && !invoice.fiscalDocument?.archive) return undefined;
    const invoiceFiscalDocumentArchives: IInvoiceFiscalDocumentArchives = {
      invoice: invoice.archive ?? undefined,
      fiscalDocument: invoice?.fiscalDocument?.archive
        ? invoice.fiscalDocument.archive
        : undefined
    };

    return (
      <BaseCardAction
        type="invoice"
        invoiceFiscalDocumentArchives={invoiceFiscalDocumentArchives}
      />
    );
  }, []);

  /**
   * Load the invoices
   */
  const loadInvoices = useCallback(async () => {
    const response = dataInvoices?.invoice.getAllWithSortedPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setTotalPages(response.pageCount);
      const invoicesList = response.result;

      if (nextPage > 1) {
        setInvoices(invoices.concat(invoicesList as InvoiceType[]));
      } else {
        setInvoices(invoicesList as InvoiceType[]);
      }
    }
  }, [dataInvoices, setInvoices]);

  /**
   * Make the where clause to get the specified invoices according to user permissions
   */
  const makeWhereInvoiceIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      if (response.result?.length === 0) {
        setWhereInvoiceIdByClients([]);

        setIsPermissionDialogAlert(true);
        setAlertDialogMessage(t(AlertMessages.noPermissionInvoices));
        setAlertDialogOpen(true);

        return;
      }

      const whereInvoiceIdsByClients: WhereInvoiceIdsByClient[] = [];

      response.result?.forEach((permission) => {
        if (permission?.construction?.client?.id) {

          let concatenatedInvoiceIds = "";

          permission?.contract?.invoices?.forEach((invoice) => {

            if (invoice?.id && invoice.id !== "") {
              concatenatedInvoiceIds = utils.concatWithSeparator(concatenatedInvoiceIds, invoice.id, "|");
            }
          });

          const whereInvoiceIdsByClient = whereInvoiceIdsByClients.find(
            (whereInvoiceIdsByClient) => whereInvoiceIdsByClient.clientId === permission.construction?.client?.id
          );

          if (whereInvoiceIdsByClient) {
            whereInvoiceIdsByClient.invoiceIds = utils.concatWithSeparator(whereInvoiceIdsByClient.invoiceIds, concatenatedInvoiceIds, "|");
          } else {
            whereInvoiceIdsByClients.push({
              clientId: permission.construction.client.id,
              invoiceIds: concatenatedInvoiceIds
            });
          }
        }
      });

      setWhereInvoiceIdByClients(whereInvoiceIdsByClients);

      const invoiceIds = whereInvoiceIdsByClients.find(
        (invoicesByClient) => invoicesByClient.invoiceIds !== ""
      );

      if (!invoiceIds) {
        setAlertDialogMessage(t(AlertMessages.noHaveInvoices));
        setAlertDialogOpen(true);
        setIsPermissionDialogAlert(false);
      }
    }
  }, [dataPermissions, setFilteredInvoices]);

  // endregion Functions
  // region UseEffects
  /**
   * Reset invoices when selected client changes
   */
  useEffect(() => {
    setInvoices([]);
    setNextPage(1);
    setTotalPages(0);
    setNoDataInCurrentClient(false);
    setNoPermissionInCurrentClient(false);
  }, [setInvoices, selectedClient]);

  /**
   * Execute invoice query when selected client changes
   */
  useEffect(() => {
    const whereInvoicesByClient = whereInvoiceIdByClients.find(
      (invoiceByClient) => invoiceByClient.clientId === selectedClient?.id
    );

    if (!whereInvoicesByClient) {
      setNoPermissionInCurrentClient(true);

      return;
    }

    setNoPermissionInCurrentClient(false);

    const whereInvoiceIds = whereInvoicesByClient?.invoiceIds;

    if (whereInvoiceIds === "") {
      setNoDataInCurrentClient(true);

      return;
    }

    setNoDataInCurrentClient(false);

    getInvoices({
      variables: {
        page: nextPage,
        perPage: limitPerPage,
        sort: [
          {
            field: "issueDate",
            sortDirection: ESort.DESC
          }
        ],
        where: [
          {
            property: "id",
            operator: EWhereClausesOperators.IN,
            value: whereInvoiceIds
          }
        ]
      }
    });
  }, [getInvoices, whereInvoiceIdByClients, nextPage, selectedClient]);

  /**
   * Process invoice permissions to get invoices ids by client
   */
  useEffect(() => {
    if (dataPermissions) makeWhereInvoiceIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereInvoiceIds]);

  /**
   * Process invoices query response when invoices query changes
   */
  useEffect(() => {
    if (dataInvoices) loadInvoices();
    if (errorInvoices) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorInvoices.message
      });
    }
  }, [addToast, dataInvoices, errorInvoices, loadInvoices]);

  /**
   * Set the filtered invoices
   */
  useEffect(() => {
    setFilteredInvoices(invoices);
  }, [invoices, setFilteredInvoices]);
  // endregion UseEffects

  return (
    <Office>
      <PageHeader url="/" title={t(CustomerMessages.applicationInvoice)} />

      <Styled.Container>
        <AlertDialog
          open={alertDialogOpen}
          title={alertDialogMessage}
          onClose={() => setAlertDialogOpen(false)}
          iconColor={isPermissionDialogAlert ? "red" : "yellow"}
        />
        <Grid container spacing={2}>
          {(loadingInvoices || loadingPermissions) && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {!loadingPermissions
            && !loadingInvoices
            && filteredInvoices
            && filteredInvoices.map((invoice) => (
              <Grid key={`grid-${invoice.id}`} item xs={12} sm={6} md={4}>
                <BaseCard
                  key={invoice.id}
                  id={invoice.id}
                  header={<BaseCardHeader type="invoice" data={invoice} />}
                  details={detailsInvoice(invoice)}
                  actions={actionInvoice(invoice)}
                />
              </Grid>
            ))}
          {!loadingPermissions && !loadingInvoices && (noPermissionInCurrentClient || noDataInCurrentClient) && (
            <AlertContentByClient
              alertMessage={noPermissionInCurrentClient
                ? t(AlertMessages.noPermissionInvoicesSpecificClient)
                : t(AlertMessages.noHaveInvoicesSpecificClient)}
              iconColor={noPermissionInCurrentClient ? "red" : "yellow"}
            />
          )}
        </Grid>
        {filteredInvoices
          && nextPage < totalPages
          && (
            <Styled.ContainerShowMoreButton>
              <ShowMoreButton onClick={handleNextPage} />
            </Styled.ContainerShowMoreButton>
          )}
      </Styled.Container>
    </Office>
  );
};

export default Invoice;
